import { useRef, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  ShieldCheck,
  Shield,
  FileText,
  Users,
  Building2,
  Wallet,
  Calculator,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Info,
  ToggleLeft,
  ClipboardList,
  Landmark,
  CreditCard,
  Hash,
  CalendarDays,
  Settings,
  BarChart3,
  Download,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";

const SECTIONS = [
  { id: "overview", label: "Compliance Overview" },
  { id: "registrations", label: "Government Registrations" },
  { id: "epf", label: "EPF (Provident Fund)" },
  { id: "esi", label: "ESI" },
  { id: "pt", label: "Professional Tax" },
  { id: "lwf", label: "Labour Welfare Fund" },
  { id: "bonus", label: "Statutory Bonus" },
  { id: "tds", label: "TDS" },
  { id: "tds-compliance", label: "TDS Compliance Center" },
  { id: "salary-engine", label: "Salary Engine PF" },
  { id: "reports", label: "Compliance Reports" },
  { id: "faq", label: "FAQs" },
  { id: "errors", label: "Common Issues" },
];

const STATUTORY_COMPONENTS = [
  { icon: Shield, label: "EPF", desc: "Provident Fund — employer mode, contribution options, and overrides", path: "/statutory/pf", accent: "#2563eb" },
  { icon: ShieldCheck, label: "ESI", desc: "Employee State Insurance — registration, coverage state, and enrollment", path: "/statutory/esi", accent: "#10b981" },
  { icon: Landmark, label: "Professional Tax", desc: "PT applicability, state and registration number", path: "/statutory/pt", accent: "#f59e0b" },
  { icon: Users, label: "Labour Welfare Fund", desc: "Employee and employer LWF contributions (optional)", path: "/statutory/lwf", accent: "#8b5cf6" },
  { icon: Calculator, label: "Statutory Bonus", desc: "Bonus percentage, salary ceiling, and payment frequency", path: "/statutory/pf", accent: "#ec4899" },
  { icon: FileText, label: "TDS", desc: "Tax Deducted at Source — regime, configuration, employee tax, compliance", path: "/settings/tds", accent: "#6366f1" },
];

const REGISTRATION_FIELDS = [
  { field: "Company PAN", format: "AAAAA9999A (5 letters + 4 digits + 1 letter)", example: "ABCDE1234F" },
  { field: "Company TAN", format: "AAAA99999A (4 letters + 5 digits + 1 letter)", example: "ABCD12345E" },
  { field: "EPFO Establishment Code", format: "MH/BAN/1234567/000 (Region/Office/Establishment/Extension)", example: "MH/BAN/1234567/000" },
  { field: "ESIC Registration Number", format: "10 to 17 digits", example: "31001234560000999" },
  { field: "PT Registration Number", format: "5-20 letters/numbers", example: "PT-KA-123456" },
  { field: "PT Enrollment Number", format: "5-20 letters/numbers", example: "ENR1234567" },
  { field: "LWF Number", format: "Free-text", example: "—" },
  { field: "GST Number", format: "15-character GSTIN", example: "27ABCDE1234F1Z5" },
  { field: "CIN Number", format: "21-character CIN", example: "U12345MH2020PTC123456" },
  { field: "Shop & Establishment Number", format: "Free-text", example: "—" },
];

const EPF_OPTIONS = [
  { label: "Include Employer Contribution in CTC", key: "include_employer_in_ctc" },
  { label: "Include EDLI in CTC", key: "include_edli_in_ctc" },
  { label: "Include Admin Charges in CTC", key: "include_admin_in_ctc" },
  { label: "Allow Employee Level PF Override", key: "allow_employee_override" },
  { label: "Allow Voluntary PF (VPF)", key: "allow_vpf" },
  { label: "Pro-rate PF Based on Worked Days", key: "prorate_pf" },
  { label: "Consider Other Earnings if PF Wage < wage ceiling after LOP", key: "consider_other_earnings_below_cap" },
  { label: "Auto Include New Employees", key: "auto_include_new_employees" },
];

const PF_CALC_ROWS = [
  { label: "Employee PF", rate: "12% of PF Wage" },
  { label: "Employer PF", rate: "12% of PF Wage" },
  { label: "EPS (Pension)", rate: "8.33% of PF Wage" },
  { label: "EPF Share", rate: "3.67% of PF Wage" },
  { label: "EDLI", rate: "0.5% of PF Wage" },
  { label: "Admin Charges", rate: "0.5% of PF Wage" },
  { label: "Inspection", rate: "0.01% of PF Wage" },
];

const TDS_WORKSPACE_STEPS = [
  { n: 1, title: "Employee Information", desc: "Deductor details, TAN, and tax settings", regimes: "Old & New" },
  { n: 2, title: "Tax Projection", desc: "Employee tax registers and projected annual liability", regimes: "Old & New" },
  { n: 3, title: "Investment Declarations", desc: "80C, 80D, and Chapter VI-A declarations", regimes: "Old only" },
  { n: 4, title: "Exemptions", desc: "HRA, LTA, and other applicable exemptions", regimes: "Old only" },
  { n: 5, title: "Proof Verification", desc: "Verify uploaded investment proofs", regimes: "Old only" },
  { n: 6, title: "Tax Calculation", desc: "Annual tax and monthly TDS computation", regimes: "Old & New" },
  { n: 7, title: "Monthly TDS", desc: "Monthly TDS deduction register", regimes: "Old & New" },
  { n: 8, title: "Reports", desc: "Deduction register, tax projection, and summary", regimes: "Old & New" },
  { n: 9, title: "Form 16", desc: "Part A & B certificates after year-end reconciliation", regimes: "Old & New" },
  { n: 10, title: "Challan Management", desc: "Government tax deposits and challan mapping", regimes: "Old & New" },
  { n: 11, title: "Reconciliation", desc: "Match deducted TDS with deposited amounts", regimes: "Old & New" },
  { n: 12, title: "Quarterly Return", desc: "Prepare and file Form 24Q", regimes: "Old & New" },
];

const TDS_COMPLIANCE_STAGES = [
  { n: 1, title: "Payroll Completed", desc: "Monthly TDS from processed payroll runs" },
  { n: 2, title: "Deposit Tax", desc: "Record government tax payment" },
  { n: 3, title: "Challan Entry", desc: "BSR code, challan number, CIN, amount" },
  { n: 4, title: "Generate 24Q", desc: "Validated, versioned quarterly return snapshot" },
  { n: 5, title: "Download Return", desc: "CSV dataset, validation report, summary" },
  { n: 6, title: "Upload to Portal", desc: "Upload to TRACES / filing provider" },
  { n: 7, title: "Acknowledgement", desc: "Ack number, token, accepted date" },
  { n: 8, title: "Generate Form 16", desc: "Individual or bulk Form 16 certificates" },
];

const TDS_REPORTS = [
  { name: "Monthly TDS Register", desc: "Month-wise TDS deductions for all employees from payroll" },
  { name: "Employee Tax Computation", desc: "Annual tax with exemptions and taxable income per employee" },
  { name: "Quarterly Return (Form 24Q)", desc: "Quarterly TDS filing status and acknowledgement" },
  { name: "Form 16", desc: "Part A & B tax certificates for employees" },
  { name: "TDS Reconciliation", desc: "Compare deducted TDS with deposited amounts" },
  { name: "Branch-wise Report", desc: "TDS summary grouped by employee branch" },
  { name: "Department-wise Report", desc: "TDS summary grouped by department" },
  { name: "Cost Center-wise Report", desc: "TDS summary by cost center" },
  { name: "Employee Tax Ledger", desc: "Running view of projected tax and monthly TDS" },
  { name: "Annual Projection", desc: "Projected income, exemptions, and tax liability for FY" },
];

const FAQ_DATA = [
  { q: "What statutory components does BrixiGo support?", a: "BrixiGo supports EPF (Provident Fund), ESI (Employee State Insurance), Professional Tax, Labour Welfare Fund, Statutory Bonus, and TDS (Tax Deducted at Source). All are configured through a guided setup wizard at /statutory/pf or the TDS module at /settings/tds." },
  { q: "Where do I configure PF?", a: "Go to Statutory Setup > EPF (or navigate to /statutory/pf). Enable EPF, enter your EPFO establishment code, choose the employer contribution mode (12% Actual PF Wage or Restrict PF Wage To the statutory ceiling — Rs.25,000 from 17 Sep 2026), and configure options like VPF, EDLI in CTC, and pro-rating." },
  { q: "How does PF calculation work in BrixiGo?", a: "PF Wage = Basic + DA (capped at the EPF wage ceiling — Rs.25,000 from 17 Sep 2026, Rs.15,000 before — if the capped mode is selected). Employee PF = 12%, Employer PF = 12% (EPS 8.33% on PF wage up to Rs.15,000; the rest goes to EPF). EDLI = 0.5%, Admin = 0.5%, Inspection = 0.01%. A built-in calculator on the EPF tab shows the breakdown." },
  { q: "Where do I configure ESI?", a: "Go to Statutory Setup > ESI (or /statutory/esi). Enable ESI, enter the ESIC registration number, select the coverage state, and configure auto-enrollment and contribution period options." },
  { q: "What is the ESI eligibility threshold?", a: "Employees with gross salary up to Rs.21,000 are eligible. Employee contribution is 0.75% and employer contribution is 3.25% of gross wages. The built-in ESI calculator shows these amounts." },
  { q: "Where do I configure Professional Tax?", a: "Go to Statutory Setup > Professional Tax (or /statutory/pt). Enable PT, select the state, enter the registration number, and choose the Effective From date. PT is deducted monthly. Slabs are statutory and system managed by BrixiGo — they cannot be edited." },
  { q: "Where do I configure TDS?", a: "Navigate to Settings > TDS (or /settings/tds). First choose between Old and New tax regime on the landing page, then use the 12-step workspace to configure deductor info, regime settings, calculation options, employee tax setup, and compliance." },
  { q: "What is the TDS Compliance Center?", a: "The Compliance Center at /settings/tds/compliance provides an 8-stage end-to-end workflow: Payroll Completed → Deposit Tax → Challan Entry → Generate 24Q → Download Return → Upload to Portal → Acknowledgement → Generate Form 16. It tracks every quarter from payroll to filing." },
  { q: "What TDS reports are available?", a: "BrixiGo has 10 TDS report types: Monthly TDS Register, Employee Tax Computation, Quarterly Return (Form 24Q), Form 16, TDS Reconciliation, Branch-wise, Department-wise, Cost Center-wise, Employee Tax Ledger, and Annual Projection." },
  { q: "How do I configure Statutory Bonus?", a: "Go to Statutory Setup > Bonus (in the setup wizard). Enable bonus, set the percentage (slider from 8.33% to 20%), and choose the payment frequency (Monthly / Quarterly / Yearly). Eligibility: salary ≤ Rs.21,000 and worked ≥ 30 days." },
  { q: "Where do I enter government registration numbers?", a: "The first step of the Statutory Setup wizard is Government Registrations. Enter Company PAN, TAN, EPFO code, ESIC number, PT registration, LWF number, GST, CIN, and Shop & Establishment number. You can also upload certificate documents (PDF, PNG, JPG)." },
  { q: "How do I view statutory reports?", a: "Navigate to Reports > Statutory (/reports/statutory) for the Statutory Reports workspace with period modes (Monthly, Quarterly, Half-Yearly, FY, Custom Range). ESI has a dedicated workspace at /reports/esi. TDS reports are in the TDS module at /settings/tds/reports." },
  { q: "What are the TDS tax slabs in BrixiGo?", a: "New Regime: 0-3L (0%), 3-6L (5%), 6-9L (10%), 9-12L (15%), 12-15L (20%), Above 15L (30%). Old Regime: 0-2.5L (0%), 2.5-5L (5%), 5-10L (20%), Above 10L (30%) with Rs.50,000 standard deduction. These are configured in the TDS constants." },
];

const ERROR_DATA = [
  { error: "Invalid EPFO Establishment Code. Must match: MH/BAN/1234567/000", cause: "The EPFO code does not match the required format: Region/Office/Establishment/Extension.", fix: "Enter the code in the correct format, e.g. MH/BAN/1234567/000.", severity: "error" },
  { error: "Invalid ESIC Registration Number. Must match: 10 to 17 digits only", cause: "The ESIC number contains non-numeric characters or is outside the length range.", fix: "Enter only digits, between 10 and 17 characters long.", severity: "error" },
  { error: "Invalid Company PAN. Must match: AAAAA9999A", cause: "The PAN does not match the format: 5 uppercase letters + 4 digits + 1 letter.", fix: "Enter a valid PAN like ABCDE1234F.", severity: "error" },
  { error: "Invalid Company TAN. Must match: AAAA99999A", cause: "The TAN does not match the format: 4 letters + 5 digits + 1 letter.", fix: "Enter a valid TAN like ABCD12345E.", severity: "error" },
  { error: "Invalid GST Number. Must match: 15-character GSTIN", cause: "The GSTIN does not match the standard 15-character format.", fix: "Enter a valid GSTIN like 27ABCDE1234F1Z5.", severity: "error" },
  { error: "Choose an employer contribution mode.", cause: "No PF contribution mode was selected when EPF is enabled.", fix: "Select either '12% Actual PF Wage' or 'Restrict PF Wage To the statutory ceiling'.", severity: "error" },
  { error: "Effective from date is required when EPF is enabled.", cause: "EPF is enabled but no effective date was set.", fix: "Select the date from which EPF should take effect.", severity: "error" },
  { error: "Coverage state is required when ESI is enabled.", cause: "ESI is enabled but no coverage state was selected.", fix: "Select the state where ESI coverage applies.", severity: "error" },
  { error: "State is required when Professional Tax is enabled.", cause: "PT is enabled but no state was selected for slab lookup.", fix: "Select the state to load the applicable PT slabs.", severity: "error" },
  { error: "BrixiGo does not have a statutory Professional Tax rule for the selected state yet.", cause: "PT is enabled for a state that has no BrixiGo statutory PT rule.", fix: "Choose a supported state from the State list, or keep PT disabled for now.", severity: "error" },
  { error: "Enter a valid employee contribution amount.", cause: "LWF employee contribution is empty or not a valid number.", fix: "Enter a valid numeric amount for the employee LWF contribution.", severity: "error" },
  { error: "Payment frequency is required.", cause: "Statutory Bonus is enabled but no payment frequency was selected.", fix: "Select Monthly, Quarterly, or Yearly payment frequency.", severity: "error" },
  { error: "Bonus % must be between 8.33 and 20.", cause: "The bonus percentage slider is outside the allowed range.", fix: "Set the bonus percentage between 8.33% and 20%.", severity: "error" },
  { error: "Fix the highlighted fields to continue.", cause: "One or more required fields have validation errors in the current wizard step.", fix: "Review all highlighted fields, correct the errors, then click Save & Continue.", severity: "warning" },
  { error: "Complete the current step before moving ahead.", cause: "Tried to skip ahead in the statutory setup wizard without saving the current step.", fix: "Complete and save the current step before proceeding to the next one.", severity: "warning" },
  { error: "TAN is required", cause: "The TAN Number field in TDS Configuration is empty.", fix: "Enter the company TAN in the Deductor Information section.", severity: "error" },
  { error: "Only JPG, JPEG, or PNG files are allowed for digital signature.", cause: "Attempted to upload a non-image file as the Form 16 digital signature.", fix: "Upload a JPG, JPEG, or PNG image file.", severity: "error" },
  { error: "Duplicate challan number(s) detected", cause: "Two or more challans in the TDS Compliance Center have the same challan number.", fix: "Remove duplicate challan entries before generating the quarterly return.", severity: "warning" },
];

export default function ComplianceHelpPage() {
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const faqRef = useRef(null);

  const lc = search.toLowerCase();

  const filteredFaqs = useMemo(
    () => FAQ_DATA.filter((f) => !lc || f.q.toLowerCase().includes(lc) || f.a.toLowerCase().includes(lc)),
    [lc]
  );

  const filteredErrors = useMemo(
    () => ERROR_DATA.filter((e) => !lc || e.error.toLowerCase().includes(lc) || e.cause.toLowerCase().includes(lc) || e.fix.toLowerCase().includes(lc)),
    [lc]
  );

  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div className="brix-page">
      {/* Hero */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-indigo">
              <ShieldCheck size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Compliance</h1>
              <p className="hs-hero-sub">PF, ESI, TDS and statutory setup in BrixiGo.</p>
            </div>
          </div>
          <form className="hs-search-wrap" onSubmit={(e) => { e.preventDefault(); if (search.trim() && faqRef.current) faqRef.current.scrollIntoView({ behavior: "smooth" }); }}>
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input type="text" className="hs-search-input" placeholder="Search — e.g. PF, ESI, TDS, professional tax..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      {/* Section Nav */}
      <div className="ah-section" style={{ padding: "16px 24px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {SECTIONS.map((s) => (
            <button key={s.id} type="button" className="hs-popular-tag" onClick={() => scrollTo(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* 1. Compliance Overview */}
      <div className="ah-section" id="overview">
        <h2 className="ah-section-title"><ShieldCheck size={20} /> Compliance Overview</h2>
        <p className="ah-section-desc">BrixiGo provides a guided statutory setup wizard and a dedicated TDS module to configure all compliance-related settings. These settings feed into payroll processing for automatic statutory deductions.</p>

        <div className="ah-content-card">
          <h3>Statutory Components</h3>
          <div className="ah-feature-grid">
            {STATUTORY_COMPONENTS.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="ah-feature-card">
                  <Icon size={20} className="ah-feature-icon" style={{ color: c.accent }} />
                  <div className="ah-feature-name">{c.label}</div>
                  <div className="ah-feature-desc">{c.desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Setup Wizard Flow</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>The statutory setup follows a guided 7-step wizard. Each step must be completed before moving to the next.</p>
          <div className="ah-workflow-steps" style={{ flexWrap: "wrap" }}>
            {["Registrations", "EPF", "ESI", "PT", "LWF", "Bonus", "Dashboard"].map((step, i, arr) => (
              <div key={step} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="ah-status-badge" style={{ background: "#dbeafe", color: "#1e40af" }}>{i + 1}. {step}</span>
                {i < arr.length - 1 && <ArrowRight size={14} style={{ color: "#94a3b8" }} />}
              </div>
            ))}
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Access & Permissions</h3>
          <div className="ah-info-grid" style={{ marginTop: 8 }}>
            <div className="ah-info-card">
              <span className="ah-status-badge" style={{ background: "#dbeafe", color: "#1e40af" }}>Admin</span>
              <span style={{ fontSize: "0.82rem", color: "#475569" }}>Full access to all statutory setup, TDS configuration, and compliance center</span>
            </div>
            <div className="ah-info-card">
              <span className="ah-status-badge" style={{ background: "#ede9fe", color: "#6d28d9" }}>HR</span>
              <span style={{ fontSize: "0.82rem", color: "#475569" }}>Access to statutory setup (PF, ESI, PT, LWF) and TDS module</span>
            </div>
            <div className="ah-info-card">
              <span className="ah-status-badge" style={{ background: "#fef3c7", color: "#92400e" }}>Finance</span>
              <span style={{ fontSize: "0.82rem", color: "#475569" }}>Access to statutory reports, ESI reports, and salary register</span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Government Registrations */}
      <div className="ah-section" id="registrations">
        <h2 className="ah-section-title"><ClipboardList size={20} /> Government Registrations</h2>
        <p className="ah-section-desc">The first step of the statutory setup wizard. Enter company-level registration numbers and upload compliance certificates.</p>

        <div className="ah-content-card">
          <h3>Registration Fields</h3>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Field</th><th>Format</th><th>Example</th></tr>
            </thead>
            <tbody>
              {REGISTRATION_FIELDS.map((f) => (
                <tr key={f.field}>
                  <td style={{ fontWeight: 600 }}>{f.field}</td>
                  <td style={{ fontSize: "0.82rem" }}>{f.format}</td>
                  <td><code style={{ fontSize: "0.8rem" }}>{f.example}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Certificate Uploads</h3>
          <p style={{ fontSize: "0.82rem", color: "#64748b" }}>You can upload compliance certificates for EPFO, ESIC, PT, LWF, and GST. Accepted formats: PDF, PNG, JPG, JPEG.</p>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Format Validation</h3>
          <p style={{ fontSize: "0.82rem", color: "#64748b" }}>All registration numbers are validated against their expected format in real-time. If a field is required and empty, the error shows: <code>"{'{Label}'} is required. Enter it like: {'{example}'}"</code>. If the format is invalid: <code>"Invalid {'{Label}'}. Must match: {'{hint}'}"</code>.</p>
        </div>
      </div>

      {/* 3. EPF */}
      <div className="ah-section" id="epf">
        <h2 className="ah-section-title"><Shield size={20} /> EPF — Provident Fund</h2>
        <p className="ah-section-desc">Configure Provident Fund settings for your organisation. Located at Statutory Setup &gt; EPF (or /statutory/pf).</p>

        <div className="ah-content-card">
          <h3>EPF Configuration Fields</h3>
          <ul className="ah-manage-list">
            <li><ToggleLeft size={14} /> <strong>Enable EPF</strong> — master toggle to enable/disable PF</li>
            <li><Hash size={14} /> <strong>EPF Establishment Code</strong> — required when enabled (format validated)</li>
            <li><Settings size={14} /> <strong>Employer Contribution Mode</strong> — required, two options:
              <ul style={{ marginTop: 4, marginLeft: 16, fontSize: "0.82rem" }}>
                <li><strong>12% Actual PF Wage</strong> — calculate on full Basic + DA</li>
                <li><strong>Restrict PF Wage To the statutory ceiling</strong> — cap PF Wage at Rs.25,000 (from 17 Sep 2026; Rs.15,000 before)</li>
              </ul>
            </li>
            <li><CalendarDays size={14} /> <strong>Effective From</strong> — date from which EPF config applies (required)</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>EPF Options (Toggles)</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8, marginTop: 8 }}>
            {EPF_OPTIONS.map((opt) => (
              <div key={opt.key} className="ah-info-card" style={{ padding: "8px 12px" }}>
                <ToggleLeft size={14} style={{ color: "#2563eb", flexShrink: 0 }} />
                <span style={{ fontSize: "0.82rem", color: "#334155" }}>{opt.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>PF Calculation (Built-in Calculator)</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>The EPF tab includes a real-time calculator. Enter Basic Pay and DA to see the breakdown.</p>
          <p style={{ marginBottom: 8, fontSize: "0.82rem", color: "#64748b" }}><strong>PF Wage</strong> = Basic + DA (capped at Rs.25,000 from 17 Sep 2026 if capped mode selected)</p>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Component</th><th>Rate</th></tr>
            </thead>
            <tbody>
              {PF_CALC_ROWS.map((r) => (
                <tr key={r.label}>
                  <td style={{ fontWeight: 600 }}>{r.label}</td>
                  <td>{r.rate}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="ah-info-card" style={{ marginTop: 12 }}>
            <Info size={14} style={{ color: "#f59e0b", flexShrink: 0 }} />
            <span style={{ fontSize: "0.82rem", color: "#475569" }}>Employees with PF wages below the wage ceiling (Rs.25,000) are statutorily eligible. The calculator shows a warning for such cases.</span>
          </div>
        </div>
      </div>

      {/* 4. ESI */}
      <div className="ah-section" id="esi">
        <h2 className="ah-section-title"><ShieldCheck size={20} /> ESI — Employee State Insurance</h2>
        <p className="ah-section-desc">Configure ESI registration, coverage state, and enrollment rules. Located at Statutory Setup &gt; ESI (or /statutory/esi).</p>

        <div className="ah-content-card">
          <h3>ESI Configuration Fields</h3>
          <ul className="ah-manage-list">
            <li><ToggleLeft size={14} /> <strong>Enable ESI</strong> — master toggle</li>
            <li><Hash size={14} /> <strong>ESI Registration Number</strong> — required when enabled (10-17 digits)</li>
            <li><Building2 size={14} /> <strong>Coverage State</strong> — required, select from Indian states</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>ESI Options (Toggles)</h3>
          <ul className="ah-manage-list">
            <li><ToggleLeft size={14} /> <strong>Auto Enroll Eligible Employees</strong></li>
            <li><ToggleLeft size={14} /> <strong>Continue Till Contribution Period Ends</strong></li>
            <li><ToggleLeft size={14} /> <strong>Employee Override Allowed</strong></li>
            <li><ToggleLeft size={14} /> <strong>Auto Remove After Salary Exceeds Limit</strong></li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>ESI Calculation (Built-in Calculator)</h3>
          <p style={{ marginBottom: 8, fontSize: "0.82rem", color: "#64748b" }}>Enter gross salary to see ESI breakdown. Eligibility: Gross ≤ Rs.21,000.</p>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Component</th><th>Rate</th></tr>
            </thead>
            <tbody>
              <tr><td style={{ fontWeight: 600 }}>Employee Contribution</td><td>0.75% of Gross Wages</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Employer Contribution</td><td>3.25% of Gross Wages</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Total</td><td>4.00% of Gross Wages</td></tr>
            </tbody>
          </table>
          <div className="ah-info-card" style={{ marginTop: 12 }}>
            <Info size={14} style={{ color: "#2563eb", flexShrink: 0 }} />
            <span style={{ fontSize: "0.82rem", color: "#475569" }}>When gross salary exceeds Rs.21,000, the calculator displays "Employee Not Eligible For ESI".</span>
          </div>
        </div>
      </div>

      {/* 5. Professional Tax */}
      <div className="ah-section" id="pt">
        <h2 className="ah-section-title"><Landmark size={20} /> Professional Tax</h2>
        <p className="ah-section-desc">Configure Professional Tax applicability and registration details. Located at Statutory Setup &gt; PT (or /statutory/pt).</p>

        <div className="ah-content-card">
          <h3>PT Configuration</h3>
          <ul className="ah-manage-list">
            <li><ToggleLeft size={14} /> <strong>Enable PT</strong> — master toggle</li>
            <li><Hash size={14} /> <strong>Registration Number</strong> — required when enabled</li>
            <li><Building2 size={14} /> <strong>State</strong> — required, determines applicable PT slabs</li>
            <li><CalendarDays size={14} /> <strong>Deduction Cycle</strong> — Monthly</li>
            <li><CalendarDays size={14} /> <strong>Effective From</strong> — each new date is saved as a new version; earlier versions stay in PT Settings History</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Statutory PT Slabs (System Managed)</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>PT slabs are statutory rules managed by BrixiGo per state and effective date. HR/Admin users cannot add, edit or delete them — the PT Settings page shows the applicable slabs read-only. Examples:</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <h4 style={{ fontSize: "0.84rem", fontWeight: 700, marginBottom: 8 }}>Maharashtra (MH)</h4>
              <table className="ah-payroll-table">
                <thead><tr><th>From</th><th>To</th><th>PT Amount</th></tr></thead>
                <tbody>
                  <tr><td>0</td><td>7,500</td><td>₹0</td></tr>
                  <tr><td>7,501</td><td>10,000</td><td>₹175</td></tr>
                  <tr><td>10,001</td><td>∞</td><td>₹200</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <h4 style={{ fontSize: "0.84rem", fontWeight: 700, marginBottom: 8 }}>Karnataka (KA)</h4>
              <table className="ah-payroll-table">
                <thead><tr><th>From</th><th>To</th><th>PT Amount</th></tr></thead>
                <tbody>
                  <tr><td>0</td><td>15,000</td><td>₹0</td></tr>
                  <tr><td>15,001</td><td>∞</td><td>₹200</td></tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="ah-info-card" style={{ marginTop: 12 }}>
            <Info size={14} style={{ color: "#2563eb", flexShrink: 0 }} />
            <span style={{ fontSize: "0.82rem", color: "#475569" }}>The PT Calculation Test lets you enter a test salary to preview which statutory slab applies and the resulting deduction. It uses the same rules as payroll and saves nothing.</span>
          </div>
        </div>
      </div>

      {/* 6. LWF */}
      <div className="ah-section" id="lwf">
        <h2 className="ah-section-title"><Users size={20} /> Labour Welfare Fund</h2>
        <p className="ah-section-desc">Configure LWF contributions. This is marked as optional in the settings navigation. Located at Statutory Setup &gt; LWF (or /statutory/lwf).</p>

        <div className="ah-content-card">
          <h3>LWF Configuration</h3>
          <ul className="ah-manage-list">
            <li><ToggleLeft size={14} /> <strong>Enable LWF</strong> — master toggle</li>
            <li><Building2 size={14} /> <strong>State</strong> — required when enabled</li>
            <li><CreditCard size={14} /> <strong>Employee Contribution (Rs.)</strong> — required, default: ₹20</li>
            <li><CreditCard size={14} /> <strong>Employer Contribution (Rs.)</strong> — required, default: ₹40</li>
            <li><CalendarDays size={14} /> <strong>Frequency</strong> — Monthly, Quarterly, Half-Yearly, or Yearly (default: Yearly)</li>
          </ul>
          <div className="ah-info-card" style={{ marginTop: 12 }}>
            <Info size={14} style={{ color: "#64748b", flexShrink: 0 }} />
            <span style={{ fontSize: "0.82rem", color: "#64748b" }}>The settings navigation notes: "Optional — not required for Andhra Pradesh and Telangana."</span>
          </div>
        </div>
      </div>

      {/* 7. Statutory Bonus */}
      <div className="ah-section" id="bonus">
        <h2 className="ah-section-title"><Calculator size={20} /> Statutory Bonus</h2>
        <p className="ah-section-desc">Configure statutory bonus percentage, eligibility, and payment frequency. Part of the statutory setup wizard.</p>

        <div className="ah-content-card">
          <h3>Bonus Configuration</h3>
          <ul className="ah-manage-list">
            <li><ToggleLeft size={14} /> <strong>Enable Bonus</strong> — master toggle</li>
            <li><Settings size={14} /> <strong>Bonus %</strong> — slider from 8.33% to 20% (step 0.01), default: 8.33%</li>
            <li><CalendarDays size={14} /> <strong>Payment Frequency</strong> — Monthly, Quarterly, or Yearly (required, default: Yearly)</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Bonus Calculation</h3>
          <p style={{ fontSize: "0.82rem", color: "#64748b", marginBottom: 8 }}>Eligibility: Salary (Basic + DA) ≤ Rs.21,000 and worked ≥ 30 days.</p>
          <ul className="ah-manage-list">
            <li><Calculator size={14} /> Eligible salary = min(Basic + DA, Rs.21,000)</li>
            <li><Calculator size={14} /> Monthly bonus = Eligible salary × Bonus % / 100</li>
          </ul>
          <p style={{ fontSize: "0.82rem", color: "#64748b", marginTop: 8 }}>The built-in calculator shows: Eligibility (Yes/No), Monthly Bonus, Annual Bonus, Projected Annual Liability, and estimated Eligible Employees.</p>
        </div>
      </div>

      {/* 8. TDS */}
      <div className="ah-section" id="tds">
        <h2 className="ah-section-title"><FileText size={20} /> TDS — Tax Deducted at Source</h2>
        <p className="ah-section-desc">A comprehensive TDS module with regime selection, configuration, employee tax setup, reports, and compliance. Located at Settings &gt; TDS (/settings/tds).</p>

        <div className="ah-content-card">
          <h3>Tax Regime Selection</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>The first step in TDS setup. Choose your organisation's default tax regime. This filters which workspace steps are shown.</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="ah-info-card" style={{ flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
              <span className="ah-status-badge" style={{ background: "#ede9fe", color: "#6d28d9" }}>Old Tax Regime</span>
              <span style={{ fontSize: "0.82rem", color: "#475569" }}>HRA exemption, 80C/80D deductions, LTA & home loan interest, Chapter VI-A benefits</span>
            </div>
            <div className="ah-info-card" style={{ flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
              <span className="ah-status-badge" style={{ background: "#dcfce7", color: "#166534" }}>New Tax Regime</span>
              <span style={{ fontSize: "0.82rem", color: "#475569" }}>Lower slab rates, standard deduction, simplified compliance, fewer declarations</span>
            </div>
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>TDS Tax Slabs</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <h4 style={{ fontSize: "0.84rem", fontWeight: 700, marginBottom: 8 }}>New Regime</h4>
              <table className="ah-payroll-table">
                <thead><tr><th>Taxable Income</th><th>Rate</th></tr></thead>
                <tbody>
                  <tr><td>0 — 3,00,000</td><td>0%</td></tr>
                  <tr><td>3,00,001 — 6,00,000</td><td>5%</td></tr>
                  <tr><td>6,00,001 — 9,00,000</td><td>10%</td></tr>
                  <tr><td>9,00,001 — 12,00,000</td><td>15%</td></tr>
                  <tr><td>12,00,001 — 15,00,000</td><td>20%</td></tr>
                  <tr><td>Above 15,00,000</td><td>30%</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <h4 style={{ fontSize: "0.84rem", fontWeight: 700, marginBottom: 8 }}>Old Regime</h4>
              <table className="ah-payroll-table">
                <thead><tr><th>Adjusted Income</th><th>Rate</th></tr></thead>
                <tbody>
                  <tr><td>0 — 2,50,000</td><td>0%</td></tr>
                  <tr><td>2,50,001 — 5,00,000</td><td>5%</td></tr>
                  <tr><td>5,00,001 — 10,00,000</td><td>20%</td></tr>
                  <tr><td>Above 10,00,000</td><td>30%</td></tr>
                </tbody>
              </table>
              <p style={{ fontSize: "0.78rem", color: "#64748b", marginTop: 4 }}>Includes Rs.50,000 standard deduction</p>
            </div>
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>TDS Workspace — 12-Step Guided Workflow</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Steps 3-5 are shown only for the Old regime. The workspace tailors its display based on your regime selection.</p>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>#</th><th>Step</th><th>Description</th><th>Regimes</th></tr>
            </thead>
            <tbody>
              {TDS_WORKSPACE_STEPS.map((s) => (
                <tr key={s.n}>
                  <td style={{ fontWeight: 600 }}>{s.n}</td>
                  <td style={{ fontWeight: 600 }}>{s.title}</td>
                  <td style={{ fontSize: "0.82rem" }}>{s.desc}</td>
                  <td><span className="ah-status-badge" style={{ background: s.regimes === "Old only" ? "#fef3c7" : "#dbeafe", color: s.regimes === "Old only" ? "#92400e" : "#1e40af" }}>{s.regimes}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>TDS Configuration (/settings/tds/configuration)</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Four configuration sections:</p>
          <div className="ah-feature-grid">
            <div className="ah-feature-card">
              <Building2 size={20} className="ah-feature-icon" style={{ color: "#2563eb" }} />
              <div className="ah-feature-name">Deductor Information</div>
              <div className="ah-feature-desc">TAN (required), deductor name (required), legal entity, state (required), address, PIN, contact, email, mobile</div>
            </div>
            <div className="ah-feature-card">
              <Settings size={20} className="ah-feature-icon" style={{ color: "#8b5cf6" }} />
              <div className="ah-feature-name">Tax Regime Settings</div>
              <div className="ah-feature-desc">Enable old/new regime toggles, default regime for new employees, regime change deadline, allow regime change</div>
            </div>
            <div className="ah-feature-card">
              <Calculator size={20} className="ah-feature-icon" style={{ color: "#10b981" }} />
              <div className="ah-feature-name">TDS Calculation Settings</div>
              <div className="ah-feature-desc">Annualized projection, previous employer income, bonus, variable pay, arrears, auto-recalculate TDS toggles</div>
            </div>
            <div className="ah-feature-card">
              <FileText size={20} className="ah-feature-icon" style={{ color: "#f59e0b" }} />
              <div className="ah-feature-name">Form 16 Settings</div>
              <div className="ah-feature-desc">Signatory name, designation, place, digital signature upload (JPG/PNG)</div>
            </div>
          </div>
          <div className="ah-info-card" style={{ marginTop: 12 }}>
            <Info size={14} style={{ color: "#2563eb", flexShrink: 0 }} />
            <span style={{ fontSize: "0.82rem", color: "#475569" }}>TDS formula: Projected Annual Salary = Current Monthly × Remaining Months + Earned Till Date. Monthly TDS = Annual Tax Liability / Remaining Months.</span>
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Employee Tax Setup (/settings/tds/employee-tax)</h3>
          <ul className="ah-manage-list">
            <li><Users size={14} /> <strong>Stat cards</strong> — Total Employees, Taxable Employees, Declarations Pending, Proofs Pending</li>
            <li><CalendarDays size={14} /> <strong>Declaration Window</strong> — start and end dates for employee declarations</li>
            <li><CalendarDays size={14} /> <strong>Proof Submission Window</strong> — start and end dates for proof uploads</li>
            <li><ClipboardList size={14} /> <strong>Employee Tax Register</strong> — table with employee name, regime, declaration status, proof status, monthly TDS</li>
            <li><CheckCircle2 size={14} /> <strong>Declaration statuses</strong> — Pending, Submitted, Approved, Rejected</li>
            <li><CheckCircle2 size={14} /> <strong>Proof statuses</strong> — Pending, Verified, Rejected</li>
            <li><Shield size={14} /> <strong>Approval</strong> — Admin can Approve or Reject employee declarations</li>
          </ul>
        </div>
      </div>

      {/* 9. TDS Compliance Center */}
      <div className="ah-section" id="tds-compliance">
        <h2 className="ah-section-title"><Shield size={20} /> TDS Compliance Center</h2>
        <p className="ah-section-desc">End-to-end quarterly compliance workflow from payroll to Form 16. Located at /settings/tds/compliance.</p>

        <div className="ah-content-card">
          <h3>8-Stage Quarterly Workflow</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Each stage unlocks the next — nothing can be filed out of order. Stages have statuses: Completed, Pending, Needs attention, Locked.</p>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Stage</th><th>Title</th><th>Description</th></tr>
            </thead>
            <tbody>
              {TDS_COMPLIANCE_STAGES.map((s) => (
                <tr key={s.n}>
                  <td style={{ fontWeight: 600 }}>{s.n}</td>
                  <td style={{ fontWeight: 600 }}>{s.title}</td>
                  <td style={{ fontSize: "0.82rem" }}>{s.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Compliance Center KPIs</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-card">
              <div className="ah-feature-name">Employees</div>
              <div className="ah-feature-desc">Total employees in the selected quarter</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">Monthly TDS</div>
              <div className="ah-feature-desc">Current payroll run TDS amount</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">Quarter TDS Payable</div>
              <div className="ah-feature-desc">Estimated or ledger-based quarterly total</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">Pending Deposit</div>
              <div className="ah-feature-desc">Outstanding tax to be deposited</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">Compliance Score</div>
              <div className="ah-feature-desc">Percentage-based compliance health indicator</div>
            </div>
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Compliance Center Tabs</h3>
          <ul className="ah-manage-list">
            <li><ClipboardList size={14} /> <strong>Quarter Workflow</strong> — the 8-stage sequential workflow</li>
            <li><BarChart3 size={14} /> <strong>Employee Tax Ledger</strong> — employee-wise monthly TDS timeline</li>
            <li><BarChart3 size={14} /> <strong>Compliance Dashboard</strong> — quarterly charts, monthly TDS trends, filing statistics</li>
            <li><FileText size={14} /> <strong>Reports</strong> — 8 compliance reports (24Q Dataset, TDS Register, Challan Register, Audit, Monthly Register, Quarterly Summary, Filing Status, Form 16)</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>24Q Pre-Generation Validation</h3>
          <p style={{ marginBottom: 8, fontSize: "0.82rem", color: "#64748b" }}>Before generating the quarterly return, the system validates:</p>
          <ul className="ah-manage-list">
            <li><CheckCircle2 size={14} /> Payroll processed for the quarter</li>
            <li><CheckCircle2 size={14} /> Tax marked as deposited</li>
            <li><CheckCircle2 size={14} /> At least one challan entered</li>
            <li><CheckCircle2 size={14} /> No duplicate challan numbers</li>
            <li><CheckCircle2 size={14} /> Challan total matches tax payable</li>
            <li><Info size={14} /> Deductor TAN configured (optional)</li>
            <li><Info size={14} /> Company PAN configured (optional)</li>
          </ul>
        </div>
      </div>

      {/* 10. Salary Engine PF */}
      <div className="ah-section" id="salary-engine">
        <h2 className="ah-section-title"><Wallet size={20} /> Salary Engine — PF Statutory Config</h2>
        <p className="ah-section-desc">The Salary V2 engine has its own PF configuration that feeds into salary calculations. Located at /salary-v2/statutory.</p>

        <div className="ah-content-card">
          <h3>Salary Engine PF Fields</h3>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Field</th><th>Default</th><th>Description</th></tr>
            </thead>
            <tbody>
              <tr><td style={{ fontWeight: 600 }}>Statutory type</td><td>PF</td><td>Currently only PF is supported</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Effective from</td><td>—</td><td>Date from which this config applies</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Wage limit</td><td>25,000</td><td>PF wage ceiling (from 17 Sep 2026; 15,000 before)</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Employee rate</td><td>12%</td><td>Employee PF contribution percentage</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Capped?</td><td>Yes</td><td>Whether PF wage is capped at the wage limit</td></tr>
              <tr><td style={{ fontWeight: 600 }}>EPS enabled?</td><td>Yes</td><td>Enable Employee Pension Scheme</td></tr>
              <tr><td style={{ fontWeight: 600 }}>VPF allowed?</td><td>Yes</td><td>Allow Voluntary Provident Fund</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Enabled?</td><td>Yes</td><td>Master enable/disable for PF in salary engine</td></tr>
            </tbody>
          </table>
          <div className="ah-info-card" style={{ marginTop: 12 }}>
            <Info size={14} style={{ color: "#2563eb", flexShrink: 0 }} />
            <span style={{ fontSize: "0.82rem", color: "#475569" }}>To enable PF in salary preview, create a PF config with pf_wage_context_key = PF_WAGE. The default settings include employer_rate (12%), eps_rate (8.33%), and epf_rate (3.67%).</span>
          </div>
        </div>
      </div>

      {/* 11. Compliance Reports */}
      <div className="ah-section" id="reports">
        <h2 className="ah-section-title"><BarChart3 size={20} /> Compliance Reports</h2>
        <p className="ah-section-desc">BrixiGo provides dedicated report workspaces for statutory compliance data.</p>

        <div className="ah-content-card">
          <h3>Statutory Reports Workspace (/reports/statutory)</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Access: Admin, HR, Finance. Supports 5 period modes: Monthly, Quarterly, Half-Yearly, Financial Year, Custom Range. Export formats: XLSX, CSV, PDF.</p>
          <ul className="ah-manage-list">
            <li><BarChart3 size={14} /> <strong>Preview</strong> — view report data with auto-generated KPIs</li>
            <li><AlertTriangle size={14} /> <strong>Exceptions</strong> — view data quality issues</li>
            <li><Download size={14} /> <strong>Download History</strong> — track past downloads with version, size, and author</li>
            <li><CalendarDays size={14} /> <strong>Scheduler</strong> — set up recurring report generation (monthly/quarterly/yearly)</li>
            <li><CheckCircle2 size={14} /> <strong>Compliance Score</strong> — graded A-E with factor breakdown</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>ESI Reports Workspace (/reports/esi)</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Dedicated ESI monthly contribution report from finalized payroll. Access: Admin, HR, Finance.</p>
          <ul className="ah-manage-list">
            <li><BarChart3 size={14} /> 9 summary cards: Total Employees, Eligible, Excluded, Gross Wages, Eligible Wages, Employee Contribution, Employer Contribution, Grand Total, Average Eligible Wage</li>
            <li><Settings size={14} /> Advanced filters: designation, branch, employee, status, ESI eligible, zero contribution, contribution period, insurance number, cost center</li>
            <li><Download size={14} /> Export: Excel, CSV, PDF, Print</li>
            <li><ClipboardList size={14} /> Tabs: Report, Validations, Download History</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>TDS Reports (/settings/tds/reports)</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>10 TDS report types plus Form 16 generation and quarterly returns.</p>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Report</th><th>Description</th></tr>
            </thead>
            <tbody>
              {TDS_REPORTS.map((r) => (
                <tr key={r.name}>
                  <td style={{ fontWeight: 600 }}>{r.name}</td>
                  <td style={{ fontSize: "0.82rem" }}>{r.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>TDS Payroll Report</h3>
          <p style={{ fontSize: "0.82rem", color: "#64748b" }}>Available in the Payroll Reports hub. Shows employee-wise taxable income and TDS deducted from finalized payroll runs. Includes 4 summary cards: Total Employees, Employees with TDS, Taxable Income, TDS Deducted. Export: Excel, CSV, PDF, Print.</p>
        </div>
      </div>

      {/* 12. FAQs */}
      <div className="ah-section" id="faq" ref={faqRef}>
        <h2 className="ah-section-title">Frequently Asked Questions</h2>
        <div className="hs-faq-list">
          {filteredFaqs.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <div key={faq.q} className="hs-faq-item">
                <button type="button" className="hs-faq-btn" onClick={() => setOpenFaq(isOpen ? null : idx)}>
                  <span className="hs-faq-q">{faq.q}</span>
                  <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: "hidden" }}>
                      <div className="hs-faq-answer">{faq.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
          {filteredFaqs.length === 0 && (
            <p style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>No FAQs match your search.</p>
          )}
        </div>
      </div>

      {/* 13. Common Issues */}
      <div className="ah-section" id="errors">
        <h2 className="ah-section-title"><AlertTriangle size={20} /> Common Compliance Issues &amp; Solutions</h2>
        <p className="ah-section-desc">Actual error messages from BrixiGo statutory modules and how to resolve them.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filteredErrors.map((err) => (
            <div key={err.error} className="ah-error-card">
              <div className="ah-error-header">
                <span className={`ah-error-badge ${err.severity === "error" ? "ah-badge-error" : "ah-badge-warning"}`}>
                  {err.severity === "error" ? "Error" : "Warning"}
                </span>
                <code className="ah-error-code">{err.error}</code>
              </div>
              <div className="ah-error-body">
                <div><strong>Cause:</strong> {err.cause}</div>
                <div><strong>Fix:</strong> {err.fix}</div>
              </div>
            </div>
          ))}
          {filteredErrors.length === 0 && (
            <p style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>No errors match your search.</p>
          )}
        </div>
      </div>

    </div>
  );
}
